// Connections — spec ch.06: "Cards for Exact Online, SnelStart, Minox,
// e-Boekhouden.nl, Microsoft 365, Gmail and UBL intake; request another
// provider … Adapter registry, per-connection capability evidence, secure
// authorisation, sync health and new-provider requests. Keep examples visible;
// disable unavailable real operations."
//
// The last clause is the design of this screen. The catalogue stays visible
// because an office deciding whether to buy this needs to see what it will
// eventually connect to — and every card that cannot be connected today says so
// and offers no button, rather than a button that answers 422.
//
// ---------------------------------------------------------------------------
// The catalogue is presentation; what can be connected is the API's answer
// ---------------------------------------------------------------------------
//
// The seven names come from ch.06 and live here, because they are a product
// statement rather than data. Which of them this deployment can actually
// authorise comes from `authorisableProviders` — what the API has an adapter
// and configuration for. The two are deliberately different lists, and a card
// is connectable only when the API says its provider is.
//
// So a build with one adapter shows seven cards and one Connect button. That is
// the honest picture, and it is also the ch.06 instruction read literally.
//
// ---------------------------------------------------------------------------
// Health is evidence, and a connection with no evidence says so
// ---------------------------------------------------------------------------
//
// `health` alone cannot tell "the provider confirmed this two minutes ago" from
// "this column has said healthy since the row was created and nobody has ever
// looked". The API reports the check beside it — when it last asked, what the
// answer was, and the code if it refused — and this screen renders the pair
// rather than the badge alone. A connection that has never been checked is
// shown as unverified, which is what it is.
//
// ---------------------------------------------------------------------------
// The credential is typed once, sent once, and never shown again
// ---------------------------------------------------------------------------
//
// There is no "view credential", no masked echo of what was typed, and the
// field is cleared as soon as the request is sent. The API never returns one —
// no response schema declares one — so there would be nothing to show; what
// matters here is that this screen does not keep its own copy in state after
// the submit either.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type {
  Administration,
  Authorised,
  Connection,
  ConnectionRevoked,
  ConnectionsPage,
  MappedAdministration,
  SyncQueued,
} from '../api/types.ts';
import type { ConnectorManifest, Connectors } from '../api/types.ts';
import { useRead } from '../state/useRead.ts';
import { useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';

/** The connector contract's operation names, as a reviewer reads them. */
const OPERATION_LABELS: Readonly<Record<string, string>> = {
  read_administrations: 'Read administrations',
  read_reference_data: 'Read ledgers, VAT codes and relations',
  read_documents: 'Read documents',
  read_bank_lines: 'Read bank lines',
  read_open_items: 'Read open items',
  create_purchase_entry: 'Book a purchase invoice',
  create_sales_entry: 'Book a sales invoice',
  attach_source: 'Attach the source document',
  import_ubl: 'Import UBL',
  reconcile_bank_line: 'Match a payment to an invoice',
};

const SUPPORT: Readonly<Record<string, { label: string; tone: string }>> = {
  supported: { label: 'Supported', tone: 'ok' },
  unverified: { label: 'Not verified', tone: 'warn' },
  unsupported: { label: 'Not available', tone: '' },
};

/**
 * ch.06's seven, as a catalogue.
 *
 * Presentation, not data: these names are what the product says it will connect
 * to. Whether any of them can be connected TODAY is the API's answer, and the
 * two are joined by the provider key below.
 */
const CATALOGUE: readonly {
  readonly provider: string;
  readonly label: string;
  readonly detail: string;
}[] = [
  {
    provider: 'eboekhouden',
    label: 'e-Boekhouden.nl',
    detail: 'Pilot B. Purchase invoices and payment matching, proven against the live API.',
  },
  {
    provider: 'minox',
    label: 'Minox',
    detail: 'Pilot A. The adapter is not written yet.',
  },
  {
    provider: 'exactonline',
    label: 'Exact Online',
    detail: 'Shown as an example. No adapter and no capability evidence.',
  },
  {
    provider: 'snelstart',
    label: 'SnelStart',
    detail: 'Shown as an example. No adapter and no capability evidence.',
  },
  {
    provider: 'microsoft365',
    label: 'Microsoft 365 mail',
    detail: 'For document intake by mailbox. The poll is not built.',
  },
  {
    provider: 'gmail',
    label: 'Gmail',
    detail: 'For document intake by mailbox. The poll is not built.',
  },
  {
    provider: 'ubl',
    label: 'UBL intake',
    detail: 'A document route rather than a connection; it belongs with Documents.',
  },
];

/** ch.02's four connection states, as something a reviewer can act on. */
const HEALTH: Readonly<Record<string, { label: string; tone: string; detail: string }>> = {
  healthy: {
    label: 'Connected',
    tone: 'ok',
    detail: 'The last check reached the accounting package.',
  },
  degraded: {
    label: 'Degraded',
    tone: 'warn',
    detail: 'The last check did not get through. Work may wait longer than usual.',
  },
  reauthorisation_required: {
    label: 'Reauthorisation needed',
    tone: 'danger',
    detail: 'The credential was refused. Connect again with a new one before anything can be sent.',
  },
  disabled: {
    label: 'Revoked',
    tone: 'neutral',
    detail: 'Nothing will be sent through this connection.',
  },
};

function health(value: string) {
  return (
    HEALTH[value] ?? {
      label: value.replace(/_/g, ' '),
      tone: 'neutral',
      detail: 'This state is not one this screen knows about.',
    }
  );
}

/** Roles the API lets connect and sync, and the narrower one for revoking. */
const CONNECT_ROLES = new Set(['owner', 'agency_operator']);
const REVOKE_ROLES = new Set(['owner']);

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly page: ConnectionsPage };

/** The step after authorising: which of ours is which of theirs. */
interface Mapping {
  readonly connectionId: string;
  readonly provider: string;
  readonly rotated: boolean;
  readonly administrations: readonly {
    readonly externalAdministrationId: string;
    readonly name: string;
    readonly mappedAdministrationId: string | null;
  }[];
}

export function ConnectionsScreen(): ReactNode {
  const workspace = useWorkspace();
  const { token, administrations } = workspace;
  const tenantId = workspace.membership.tenantId;
  const role = workspace.membership.role;

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [connecting, setConnecting] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The adapters this build ships and what each declares it can do — the
  // capability manifest from packages/accounting/registry.ts, as published.
  const registry = useRead<Connectors>('/connectors', 'the connector registry');
  const manifests = new Map((registry.data?.connectors ?? []).map((entry) => [entry.provider, entry]));

  const reload = useCallback(async () => {
    try {
      const page = await request<ConnectionsPage>(tenantPath(tenantId, '/connections'), { token });
      setLoad({ kind: 'ready', page });
    } catch (caught) {
      setLoad({
        kind: 'error',
        message:
          caught instanceof ApiError && caught.isAbsent
            ? 'That office is not available to this session.'
            : 'The connections could not be read.',
      });
    }
  }, [tenantId, token]);

  useEffect(() => {
    let cancelled = false;
    // Reset first: another office's connections must not stay on screen under
    // the new office's name while the next request is in flight.
    setLoad({ kind: 'loading' });
    setConnecting(null);
    setMapping(null);
    setNotice(null);
    void (async () => {
      if (cancelled) return;
      await reload();
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const page = load.kind === 'ready' ? load.page : null;
  const authorisable = useMemo(
    () => new Map((page?.authorisableProviders ?? []).map((entry) => [entry.provider, entry.auth])),
    [page],
  );

  const authorise = useCallback(
    async (provider: string, credential: { accessToken: string; country: string; currency: string }) => {
      setBusy(true);
      setNotice(null);
      try {
        const answer = await request<Authorised>(tenantPath(tenantId, '/connections/authorise'), {
          method: 'POST',
          token,
          body:
            authorisable.get(provider) === 'oauth2'
              ? // Where the provider sends the customer back to. Our own origin,
                // and the API checks it against its allowlist as well.
                { provider, redirectUri: `${window.location.origin}/connections` }
              : { provider, credential },
        });

        if (answer.kind === 'redirect') {
          // Unreachable in this build — no provider is `oauth2` — and written
          // because the endpoint can answer it and a screen that ignored the
          // discriminator would silently do nothing.
          window.location.assign(answer.authorisationUrl);
          return;
        }

        setConnecting(null);
        setMapping({
          connectionId: answer.connectionId,
          provider: answer.provider,
          rotated: answer.rotated,
          administrations: answer.administrations,
        });
        await reload();
      } catch (caught) {
        setNotice(connectMessage(caught));
      } finally {
        setBusy(false);
      }
    },
    [tenantId, token, authorisable, reload],
  );

  const map = useCallback(
    async (connectionId: string, administrationId: string, externalAdministrationId: string) => {
      setBusy(true);
      setNotice(null);
      try {
        await request<MappedAdministration>(
          tenantPath(tenantId, `/connections/${encodeURIComponent(connectionId)}/map-administration`),
          {
            method: 'POST',
            token,
            idempotencyKey: crypto.randomUUID(),
            body: { administrationId, externalAdministrationId },
          },
        );
        await reload();
        setMapping((current) =>
          current === null
            ? null
            : {
                ...current,
                administrations: current.administrations.map((administration) =>
                  administration.externalAdministrationId === externalAdministrationId
                    ? { ...administration, mappedAdministrationId: administrationId }
                    : administration,
                ),
              },
        );
      } catch (caught) {
        setNotice(connectMessage(caught));
      } finally {
        setBusy(false);
      }
    },
    [tenantId, token, reload],
  );

  const sync = useCallback(
    async (connectionId: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const queued = await request<SyncQueued>(
          tenantPath(tenantId, `/connections/${encodeURIComponent(connectionId)}/sync`),
          { method: 'POST', token, body: {} },
        );
        // Queued is the strongest thing that can be said: the check happens in
        // the worker, and this screen will show its answer when it has one.
        const already = queued.queued.filter((entry) => entry.alreadyQueued).length;
        setNotice(
          `Queued a check for ${queued.queued.length} administration${queued.queued.length === 1 ? '' : 's'}` +
            (already === 0 ? '.' : `, ${already} of which was already waiting.`) +
            ' The result appears here once the worker has asked the provider.',
        );
        await reload();
      } catch (caught) {
        setNotice(connectMessage(caught));
      } finally {
        setBusy(false);
      }
    },
    [tenantId, token, reload],
  );

  const revoke = useCallback(
    async (connectionId: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const revoked = await request<ConnectionRevoked>(
          tenantPath(tenantId, `/connections/${encodeURIComponent(connectionId)}/revoke`),
          { method: 'POST', token, body: {} },
        );
        setNotice(
          revoked.inFlightOperations === 0
            ? 'The connection and its stored credential are revoked. Nothing was in flight.'
            : `The connection and its stored credential are revoked. ${revoked.inFlightOperations} operation${
                revoked.inFlightOperations === 1 ? '' : 's'
              } already on the way will fail; open Documents to see where each one got to.`,
        );
        await reload();
      } catch (caught) {
        setNotice(connectMessage(caught));
      } finally {
        setBusy(false);
      }
    },
    [tenantId, token, reload],
  );

  const mayConnect = CONNECT_ROLES.has(role);
  const mayRevoke = REVOKE_ROLES.has(role);
  const live = (page?.connections ?? []).filter((connection) => connection.revokedAt === null);
  const revoked = (page?.connections ?? []).filter((connection) => connection.revokedAt !== null);

  return (
    <>
      <div className="heading">
        <h1>Connections</h1>
        <p>
          Where your bookkeeping actually lands. Nothing is sent to an accounting package this
          office has not connected.
        </p>
      </div>

      {load.kind === 'error' ? (
        <p className="error-banner" role="alert">
          {load.message}
        </p>
      ) : null}
      {notice === null ? null : (
        <div className="notice" role="status" data-testid="connection-notice">
          <div className="grow">{notice}</div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Connected</h2>
            <p>What this office has authorised, and what the provider last said about it</p>
          </div>
          <span className="badge">{live.length}</span>
        </div>

        {load.kind === 'loading' ? (
          <div className="empty">
            <p>Reading your connections…</p>
          </div>
        ) : live.length === 0 ? (
          <div className="empty">
            <h3>Nothing connected</h3>
            <p>
              Connect an accounting package below. Until then documents can arrive and be prepared,
              and nothing can be sent anywhere.
            </p>
          </div>
        ) : (
          live.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              administrations={administrations}
              busy={busy}
              mayConnect={mayConnect}
              mayRevoke={mayRevoke}
              role={role}
              onSync={() => void sync(connection.id)}
              onRevoke={() => void revoke(connection.id)}
            />
          ))
        )}

        {revoked.length === 0 ? null : (
          <div className="card-foot">
            {revoked.length} revoked connection{revoked.length === 1 ? ' is' : 's are'} kept for the
            record. What was posted through them keeps its provenance.
          </div>
        )}
      </div>

      {mapping === null ? null : (
        <MapAdministrations
          mapping={mapping}
          administrations={administrations}
          busy={busy}
          onMap={(administrationId, externalAdministrationId) =>
            void map(mapping.connectionId, administrationId, externalAdministrationId)
          }
          onClose={() => setMapping(null)}
        />
      )}

      <div className="card section-space">
        <div className="card-head">
          <div>
            <h2>Accounting packages and mail</h2>
            <p>Everything this product connects to, and what it can connect to today</p>
          </div>
        </div>
        <div className="card-body">
          <div className="grid3">
            {CATALOGUE.map((entry) => (
              <ProviderCard
                key={entry.provider}
                entry={entry}
                auth={authorisable.get(entry.provider)}
                manifest={manifests.get(entry.provider)}
                manifestError={registry.error}
                connected={live.some((connection) => connection.provider === entry.provider)}
                mayConnect={mayConnect}
                busy={busy}
                connecting={connecting === entry.provider}
                onOpen={() => setConnecting(entry.provider)}
                onCancel={() => setConnecting(null)}
                onSubmit={(credential) => void authorise(entry.provider, credential)}
              />
            ))}
          </div>
        </div>
        <div className="card-foot">
          {/* No "request another provider" button: the requests family has no
              endpoint, and a button that did nothing would be the demo control
              V07 asks to remove rather than reproduce. */}
          Another package? A connector is a discovery task rather than a switch — ask your agency
          contact, and it becomes a piece of work with capability evidence behind it.
        </div>
      </div>

      <div className="card section-space">
        <div className="card-head">
          <h2>What this screen cannot tell you yet</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>Capability evidence beyond the manifest.</b> Each card shows what the adapter
              declares for a new administration; an administration&rsquo;s own manifest (which
              depends on how its bank lines arrive) is on GET /administrations. The evidence
              files behind each entry are referenced by id, not shown here.
            </li>
            <li>
              <b>What a sync actually crawls.</b> Today it confirms the credential and the
              administration identity and records the answer. Reading documents and bank lines
              through it is a later slice.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function ConnectionRow({
  connection,
  administrations,
  busy,
  mayConnect,
  mayRevoke,
  role,
  onSync,
  onRevoke,
}: {
  readonly connection: Connection;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly mayConnect: boolean;
  readonly mayRevoke: boolean;
  readonly role: string;
  readonly onSync: () => void;
  readonly onRevoke: () => void;
}): ReactNode {
  const state = health(connection.health);
  const label =
    CATALOGUE.find((entry) => entry.provider === connection.provider)?.label ?? connection.provider;

  return (
    <div className="row" data-testid={`connection-${connection.id}`}>
      <div className="grow">
        <strong>{label}</strong>
        <p>
          {/* The check, beside the badge. A connection whose provider has never
              been asked is unverified, whatever the health column says. */}
          {connection.lastCheckedAt === null ? (
            <span data-testid="never-checked">
              Never checked — nothing has asked {label} whether this credential works.
            </span>
          ) : connection.lastCheckOk === true ? (
            <>Confirmed {format.dateTime(connection.lastCheckedAt)}</>
          ) : (
            <>
              Refused {format.dateTime(connection.lastCheckedAt)}
              {connection.lastCheckCode === null ? '' : ` · ${connection.lastCheckCode}`}
            </>
          )}
        </p>
        <p>
          {connection.administrations.length === 0 ? (
            'No administration is mapped onto it yet, so nothing runs through it.'
          ) : (
            <>
              {connection.administrations.length} administration
              {connection.administrations.length === 1 ? '' : 's'} ·{' '}
              {connection.administrations
                .map(
                  (mapping) =>
                    `${mapping.name}${
                      mapping.lastSyncedAt === null
                        ? ' (never synced)'
                        : ` (synced ${format.day(mapping.lastSyncedAt)})`
                    }`,
                )
                .join(', ')}
            </>
          )}
        </p>
        {administrations.length === 0 ? null : null}
      </div>
      <span className={`badge ${state.tone}`} title={state.detail}>
        {state.label}
      </span>
      <button
        type="button"
        className="btn"
        disabled={busy || !mayConnect}
        title={mayConnect ? 'Ask the provider to confirm this credential' : `Your role (${role}) may not do this.`}
        onClick={onSync}
      >
        Check now
      </button>
      <button
        type="button"
        className="btn danger"
        disabled={busy || !mayRevoke}
        title={mayRevoke ? 'Revoke the credential and stop new work' : 'Only an office owner may revoke.'}
        onClick={onRevoke}
      >
        Revoke
      </button>
    </div>
  );
}

/**
 * One catalogue card.
 *
 * ch.06: "Keep examples visible; disable unavailable real operations." So every
 * card is here, and a provider this deployment cannot authorise has no form and
 * no button — with the reason, rather than a control that answers 422.
 */
function ProviderCard({
  entry,
  auth,
  manifest,
  manifestError,
  connected,
  mayConnect,
  busy,
  connecting,
  onOpen,
  onCancel,
  onSubmit,
}: {
  readonly entry: { provider: string; label: string; detail: string };
  readonly auth: 'api_token' | 'oauth2' | undefined;
  readonly manifest: ConnectorManifest | undefined;
  readonly manifestError: string | null;
  readonly connected: boolean;
  readonly mayConnect: boolean;
  readonly busy: boolean;
  readonly connecting: boolean;
  readonly onOpen: () => void;
  readonly onCancel: () => void;
  readonly onSubmit: (credential: { accessToken: string; country: string; currency: string }) => void;
}): ReactNode {
  return (
    <div className="card connector" data-testid={`provider-${entry.provider}`}>
      <div className="card-body">
        <div className="lg-mark" aria-hidden="true">
          {initialsOf(entry.label)}
        </div>
        <div className="between">
          <b>{entry.label}</b>
          {connected ? (
            <span className="badge ok">Connected</span>
          ) : auth === undefined ? (
            <span className="badge">Not available</span>
          ) : (
            <span className="badge blue">Available</span>
          )}
        </div>
        <p className="muted small" style={{ margin: '8px 0 12px' }}>
          {entry.detail}
        </p>
        <Manifest manifest={manifest} error={manifestError} provider={entry.provider} />

        {auth === undefined ? (
          /* Visible and disabled, which is the instruction read literally. */
          <p className="muted small" data-testid={`unavailable-${entry.provider}`}>
            This build cannot connect {entry.label}: there is no adapter for it, and a connector is
            a discovery task with capability evidence rather than a switch.
          </p>
        ) : !mayConnect ? (
          <p className="muted small">Your role may not connect an accounting package.</p>
        ) : connecting ? (
          <CredentialForm auth={auth} busy={busy} onCancel={onCancel} onSubmit={onSubmit} />
        ) : (
          <button type="button" className="btn primary" disabled={busy} onClick={onOpen}>
            {connected ? 'Reconnect' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * What the adapter declares it can do, operation by operation, with the
 * constraints it records. Absent means this build has no adapter for the
 * package — which is not the same as an adapter that can do nothing.
 */
function Manifest({
  manifest,
  error,
  provider,
}: {
  readonly manifest: ConnectorManifest | undefined;
  readonly error: string | null;
  readonly provider: string;
}): ReactNode {
  if (manifest === undefined) {
    return error === null ? null : <p className="muted small">{error}</p>;
  }
  return (
    <details style={{ marginBottom: 12 }} data-testid={`manifest-${provider}`}>
      <summary className="small">
        <b>Adapter {manifest.version}</b> ·{' '}
        {manifest.capabilities.filter((capability) => capability.support === 'supported').length} of{' '}
        {manifest.capabilities.length} operations supported
      </summary>
      <p className="muted small" style={{ marginTop: 6 }}>For an administration with no configuration yet.</p>
      <ul className="capabilities">
        {manifest.capabilities.map((capability) => {
          const support = SUPPORT[capability.support] ?? { label: capability.support, tone: 'warn' };
          return (
            <li key={capability.operation} title={capability.constraints.join(' · ') || undefined}>
              <span>{OPERATION_LABELS[capability.operation] ?? capability.operation}</span>
              <span className={`badge ${support.tone}`}>{support.label}</span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/** "e-Boekhouden.nl" → "eB", "Microsoft 365 mail" → "M3": the design's marks. */
function initialsOf(label: string): string {
  if (label.startsWith('e-Boekhouden')) return 'eB';
  const words = label.split(/[\s.-]+/).filter(Boolean);
  return words.length > 1 ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase() : label.slice(0, 2).toUpperCase();
}

/**
 * The credential, typed once.
 *
 * Country and currency are asked for rather than assumed: the country decides
 * the VAT-code set and the provider cross-checks it, and e-Boekhouden's API has
 * no currency field anywhere, so an unconfigured currency would be an assumed
 * EUR. Both are the operator's confirmation and the API refuses without them.
 */
function CredentialForm({
  auth,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly auth: 'api_token' | 'oauth2';
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (credential: { accessToken: string; country: string; currency: string }) => void;
}): ReactNode {
  const [accessToken, setAccessToken] = useState('');
  const [country, setCountry] = useState('NL');
  const [currency, setCurrency] = useState('EUR');

  if (auth === 'oauth2') {
    return (
      <div className="stack">
        <p className="muted small">
          You will be sent to the provider to authorise this connection. We never see your password,
          and the grant is stored encrypted.
        </p>
        <button
          type="button"
          className="btn primary"
          disabled={busy}
          onClick={() => onSubmit({ accessToken: '', country, currency })}
        >
          Continue to the provider
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (accessToken.trim() === '') return;
        onSubmit({ accessToken: accessToken.trim(), country, currency });
        // Cleared as soon as it is sent. The API never returns a credential, and
        // this screen does not keep one either.
        setAccessToken('');
      }}
    >
      <label className="field">
        API token
        <input
          type="password"
          value={accessToken}
          aria-label="API token"
          autoComplete="off"
          onChange={(event) => setAccessToken(event.target.value)}
          placeholder="From your accounting package"
        />
      </label>
      <div className="form-row">
        <label className="field">
          Country
          <select aria-label="Country" value={country} onChange={(event) => setCountry(event.target.value)}>
            <option value="NL">Netherlands</option>
            <option value="BE">Belgium</option>
          </select>
        </label>
        <label className="field">
          Currency
          <select aria-label="Currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </label>
      </div>
      <p className="muted small" style={{ marginBottom: 10 }}>
        The country decides which VAT codes may be used and is checked against the provider&rsquo;s
        own answer. The token is stored encrypted and is never shown again.
      </p>
      <div className="flex">
        <button type="submit" className="btn primary" disabled={busy || accessToken.trim() === ''}>
          {busy ? 'Checking…' : 'Connect'}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * ch.03's onboarding step 5: which of ours is which of theirs.
 *
 * The provider said which administrations the credential reaches, and only an
 * operator can say which of the office's administrations each one is. Nothing
 * is guessed by name: two administrations can legitimately share one, and a
 * wrong mapping sends a customer's bookings into somebody else's ledger.
 */
function MapAdministrations({
  mapping,
  administrations,
  busy,
  onMap,
  onClose,
}: {
  readonly mapping: Mapping;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly onMap: (administrationId: string, externalAdministrationId: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const [choice, setChoice] = useState<Readonly<Record<string, string>>>({});

  return (
    <div className="card section-space" data-testid="map-administrations">
      <div className="card-head">
        <div>
          <h2>{mapping.rotated ? 'Credential replaced' : 'Connected'}</h2>
          <p>
            {mapping.rotated
              ? 'The new credential replaced the old one on this connection. Map any administration that is still unmapped.'
              : 'Now say which of your administrations is which of theirs. Nothing runs through the connection until you do.'}
          </p>
        </div>
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>At {mapping.provider}</th>
              <th>Your administration</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {mapping.administrations.map((administration) => (
              <tr key={administration.externalAdministrationId}>
                <td>
                  <b>{administration.name === '' ? 'Unnamed' : administration.name}</b>
                  {/* The provider's opaque id, shown because an operator with two
                      similarly named administrations needs to tell them apart. */}
                  <small>{administration.externalAdministrationId}</small>
                </td>
                <td>
                  {administration.mappedAdministrationId !== null ? (
                    <span className="muted">
                      {administrations.find(
                        (entry) => entry.id === administration.mappedAdministrationId,
                      )?.name ?? 'Mapped'}
                    </span>
                  ) : (
                    <select
                      aria-label={`Map ${administration.externalAdministrationId}`}
                      value={choice[administration.externalAdministrationId] ?? ''}
                      onChange={(event) =>
                        setChoice((current) => ({
                          ...current,
                          [administration.externalAdministrationId]: event.target.value,
                        }))
                      }
                    >
                      <option value="">Not mapped</option>
                      {administrations.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.name}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {administration.mappedAdministrationId !== null ? (
                    <span className="badge ok">Mapped</span>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      disabled={
                        busy || (choice[administration.externalAdministrationId] ?? '') === ''
                      }
                      onClick={() =>
                        onMap(
                          choice[administration.externalAdministrationId] ?? '',
                          administration.externalAdministrationId,
                        )
                      }
                    >
                      Map
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-foot">
        A mapping is not something to change casually: the operations already posted through one are
        evidence about a ledger, so moving it is refused and needs its own decision.
      </div>
    </div>
  );
}

/** One refusal, in words, without claiming more than the status says. */
function connectMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) return 'The request could not be completed.';
  if (caught.status === 403) return 'Your role does not allow this action.';
  if (caught.isAbsent) return 'This is no longer available to your session.';
  if (caught.isStale) {
    return 'This changed since the screen was loaded. The latest is shown now.';
  }
  if (caught.status === 422) {
    const credential = caught.fieldErrors.find((error) => error.field === 'credential');
    if (credential !== undefined) {
      // The provider's own refusal code, which is the useful thing here: an
      // office can quote it when they ask the provider what is wrong.
      return `The accounting package did not accept that credential (${credential.code}). Check the token and the country, and try again.`;
    }
    const fields = caught.fieldErrors.map((error) => error.field).join(', ');
    return `The request was refused: ${fields === '' ? 'validation failed' : fields}.`;
  }
  if (caught.status >= 500) {
    // Deliberately not "your token is wrong": a provider that could not be
    // reached has said nothing about the credential.
    return 'We could not reach the accounting package to check this. Nothing was stored; try again in a moment.';
  }
  return caught.message;
}
